import {
    defaultLiveMonitoringListSortDirection,
    defaultLiveMonitoringListSortField,
} from 'lib/constants';
import {
    ConsumersDevicesSession,
    ConsumersUserSession,
    DeviceUsersSession,
    EfficiencyAccountsTableUserSession,
    IssueTableUserSession,
    OrganizationDeliveryTypesSession,
    OrganizationGroupSession,
    LowBatteryMonitorTableSession,
    OfflineMonitorTableSession,
    ShippedMonitorTableSession,
    SleepingMonitorTableSession,
    UserDeviceDeliveryTicketsSession,
    UserDeviceTicketsSession,
    UserMapSession,
    UserPreferencesSession,
    UserSession,
} from 'types/User/firebaseTypes';

const defaultDeviceColumnOrder: string[] = [
    'device_health',
    'tank',
    'duf_7',
    'customer_name',
    'address',
    'l30',
    'battery',
];

// Set default column visibility. To work as expected with DataTable "Reset" functionality on columns options, these boolean values need
// to match the 'hideByDefault' value associated with the DataTableColumn (false is the default value of hideByDefault).
const defaultDeviceColumns = {
    device_health: true,
    tank: true,
    duf_7: true,
    customer_name: true,
    address: true,
    l30: true,
    battery: true,
};

export const defaultConsumptionTypFilters = {
    bulk_storage: true,
    commercial_industrial: true,
    cooking: true,
    fireplace: true,
    generator: true,
    heating: true,
    hot_water: true,
    laundry_dryer: true,
    pool: true,
    retail_fill_up: true,
    backup_heating: true,
};

/** Returns an entire user session object with default values set. */
export const createDefaultSession = (): UserSession => {
    return {
        accounts: {
            sort: {
                direction: 'asc',
                field: 'lastReading.tank',
            },
        },
        device: {
            columns: defaultDeviceColumns,
            col_order: defaultDeviceColumnOrder,
            filters: {
                // No default delivery types (these are user-provided custom values).
                delivery_type: {},
                consumptionType: defaultConsumptionTypFilters,
                misc: {
                    active: true,
                },
            },
            sort: {
                currentSortDirection: defaultLiveMonitoringListSortDirection,
                currentSortField: defaultLiveMonitoringListSortField,
            },
            activeView: 'list',
        },
        ticket: {
            delivery: {
                filters: {
                    assignedToMe: false,
                    status: {
                        backlog: true,
                        done: false,
                        in_progress: true,
                        planned: true,
                        archived: false,
                        assigned: false,
                    },
                },
            },
            filters: {
                assignedToMe: false,
                status: {
                    backlog: true,
                    done: false,
                    in_progress: true,
                    planned: true,
                    archived: false,
                    assigned: false,
                },
                ticketCategory: {
                    issue: true,
                    delivery: false,
                },
                ticketType: {
                    missing_capacity: true,
                    missing_address: true,
                    missing_orientation: true,
                    offline: true,
                    battery_crit: true,
                    battery_warn: true,
                    stuck_gauge: true,
                },
            },
        },
        // No default groups (these are user-provided custom values).
        groups: [],
        issues: createDefaultIssuesUserSession(),
        userPreferences: createDefaultUserPreferencesSession(),
        efficiencyAccounts: createDefaultEfficiencyAccountsUserSession(),
        map: createDefaultMapSession(),
        DeviceTickets: createDefaultDeviceTicketsSession(),
        DeviceDeliveryTickets: createDefaultDeviceDeliveriesSession(),
        organizationGroups: createDefaultOrganizationGroupSession(),
        organizationDeliveryTypes: createDefaultDeliveryTypesSession(),
        deviceUsers: createDefaultDeviceUsersSession(),
        offlineMonitors: createDefaultOfflineMonitorSession(),
        lowBatteryMonitors: createDefaultLowBatteryMonitorSession(),
        sleepingMonitors: createDefaultSleepingMonitorSession(),
        shippedMonitors: createDefaultShippedMonitorSession(),
        consumers: createDefaultConsumersUserSession(),
        consumerDevices: createDefaultConsumerDevicesSession(),
    };
};

/** Returns a user session entry for the Device > Deliveries page with default values. */
export const createDefaultDeviceDeliveriesSession =
    (): UserDeviceDeliveryTicketsSession => {
        const sessionEntry: UserDeviceDeliveryTicketsSession = {
            col_order: [
                'status',
                'dateDelivered',
                'gallonsDelivered',
                'plannedDeliveryDate',
                'plannedDeliveryAmount',
                'createdBy',
                'device.lastReading.tank',
                'device.ullage',
                'lastFillup',
                'lastFillup.diff',
                'lastFillup.amount',
            ],
            // Set default column visibility. To work as expected with DataTable "Reset" functionality on columns options, these boolean values need
            // to match the 'hideByDefault' value associated with the DataTableColumn (false is the default value of hideByDefault).
            columns: {
                status: true,
                dateDelivered: true,
                gallonsDelivered: true,
                plannedDeliveryDate: true,
                plannedDeliveryAmount: true,
                createdBy: true,
                'device.lastReading.tank': true,
                'device.ullage': true,
                lastFillup: true,
                'lastFillup.diff': true,
                'lastFillup.amount': true,
            },
        };
        return sessionEntry;
    };

/** Returns a user session entry for the Device > Tickets page with default values. */
export const createDefaultDeviceTicketsSession =
    (): UserDeviceTicketsSession => {
        const sessionEntry: UserDeviceTicketsSession = {
            col_order: [
                'status',
                'name',
                'taggedUsers',
                'ticketTrigger',
                'createdBy',
            ],
            // Set default column visibility. To work as expected with DataTable "Reset" functionality on columns options, these boolean values need
            // to match the 'hideByDefault' value associated with the DataTableColumn (false is the default value of hideByDefault).
            columns: {
                status: true,
                name: true,
                taggedUsers: true,
                ticketTrigger: true,
                createdBy: true,
            },
        };
        return sessionEntry;
    };

/** Returns a user session entry for Map page with default values. */
export const createDefaultMapSession = (): UserMapSession => {
    const mapSession: UserMapSession = {
        doCluster: true,
        v0: true,
        v20: true,
        v40: true,
        v60: true,
        v80: true,
    };
    return mapSession;
};

/** Returns a user session entry for user preferences with default values. */
export const createDefaultUserPreferencesSession =
    (): UserPreferencesSession => {
        const preferencesSession: UserPreferencesSession = { theme: '' };
        return preferencesSession;
    };

/** Returns a user session entry for the Issues page with default values. */
export const createDefaultIssuesUserSession = (): IssueTableUserSession => {
    const issueSession: IssueTableUserSession = {
        // Default column ordering.
        col_order: [
            'dateCreated_iso',
            'status',
            'name',
            'ticketTrigger',
            'taggedUsers',
            'device.customer_name',
            'device.name',
            'device.shortDeviceId',
            'device.address',
            'createdBy',
        ],
        // Set default column visibility. To work as expected with DataTable "Reset" functionality on columns options, these boolean values need
        // to match the 'hideByDefault' value associated with the DataTableColumn (false is the default value of hideByDefault).
        columns: {
            dateCreated_iso: true,
            status: true,
            name: true,
            ticketTrigger: true,
            taggedUsers: true,
            'device.customer_name': true,
            'device.name': true,
            'device.shortDeviceId': true,
            'device.address': true,
            createdBy: true,
        },
    };
    return issueSession;
};

/** Returns a user session entry for the Efficiency > Accounts page with default values. */
export const createDefaultEfficiencyAccountsUserSession =
    (): EfficiencyAccountsTableUserSession => {
        const accountsSession = {
            // Default column ordering. NOTE: this ordering will likely change SW-4412 and SW-4413
            col_order: [
                'customer_name',
                'name',
                'accountId',
                'count',
                'capacity',
                'average',
                'avoided',
                'saved',
            ],
            // Set default column visibility. To work as expected with DataTable "Reset" functionality on columns options, these boolean values need
            // to match the 'hideByDefault' value associated with the DataTableColumn (false is the default value of hideByDefault).
            columns: {
                customer_name: true,
                name: false,
                accountId: true,
                count: true,
                capacity: true,
                average: true,
                avoided: true,
                saved: true,
            },
        };
        return accountsSession;
    };

const defaultOrganizationGroupColumns = {
    name: true,
    description: true,
    remove: true,
};

const defaultOrganizationGroupColOrder = ['name', 'description', 'remove'];

export const createDefaultOrganizationGroupSession =
    (): OrganizationGroupSession => {
        const session: OrganizationGroupSession = {
            col_order: defaultOrganizationGroupColOrder,
            columns: defaultOrganizationGroupColumns,
        };
        return session;
    };

const defaultDeliveryTypesColumns = {
    deliveryTypeName: true,
    willCall: true,
    remove: true,
};
const defaultDeliveryTypesColOrder = ['deliveryTypeName', 'willCall', 'remove'];

export const createDefaultDeliveryTypesSession =
    (): OrganizationDeliveryTypesSession => {
        const session: OrganizationDeliveryTypesSession = {
            col_order: defaultDeliveryTypesColOrder,
            columns: defaultDeliveryTypesColumns,
        };
        return session;
    };

const defaultDeviceUsersColumns = {
    name: true,
    email: true,
    address: false,
    phone: false,
    lastLogin: true,
    readOnly: true,
    uid: false,
};
const defaultDeviceUsersColOrder = [
    'name',
    'email',
    'address',
    'phone',
    'lastLogin',
    'readOnly',
    'uid',
];

export const createDefaultDeviceUsersSession = (): DeviceUsersSession => {
    const session: DeviceUsersSession = {
        col_order: defaultDeviceUsersColOrder,
        columns: defaultDeviceUsersColumns,
    };
    return session;
};

const defaultShippedMonitorColumns = {
    orderDate: true,
    shortDeviceId: true,
    trackingLink: true,
    status: true,
};
const defaultShippedMonitorColOrder = [
    'orderDate',
    'shortDeviceId',
    'trackingLink',
    'status',
];

export const createDefaultShippedMonitorSession =
    (): ShippedMonitorTableSession => {
        const session: ShippedMonitorTableSession = {
            col_order: defaultShippedMonitorColOrder,
            columns: defaultShippedMonitorColumns,
        };
        return session;
    };

const defaultLowBatteryMonitorColumns = {
    lastReading: true,
    health: true,
    shortDeviceId: true,
    name: true,
    customer_name: true,
    address: true,
};
const defaultLowBatteryMonitorColOrder = [
    'lastReading',
    'health',
    'shortDeviceId',
    'name',
    'customer_name',
    'address',
];
export const createDefaultLowBatteryMonitorSession =
    (): LowBatteryMonitorTableSession => {
        const session: LowBatteryMonitorTableSession = {
            col_order: defaultLowBatteryMonitorColOrder,
            columns: defaultLowBatteryMonitorColumns,
        };
        return session;
    };

const defaultOfflineMonitorColumns = {
    health: true,
    shortDeviceId: true,
    name: true,
    address: true,
    customer_name: true,
    status: true,
};
const defaultOfflineMonitorColOrder = [
    'health',
    'shortDeviceId',
    'name',
    'address',
    'customer_name',
    'status',
];
export const createDefaultOfflineMonitorSession =
    (): OfflineMonitorTableSession => {
        const session: OfflineMonitorTableSession = {
            col_order: defaultOfflineMonitorColOrder,
            columns: defaultOfflineMonitorColumns,
        };
        return session;
    };

const defaultSleepingMonitorColumns = {
    shortDeviceId: true,
    name: true,
    address: true,
    customer_name: true,
    status: true,
};
const defaultSleepingMonitorColOrder = [
    'shortDeviceId',
    'name',
    'address',
    'customer_name',
    'status',
];
export const createDefaultSleepingMonitorSession =
    (): SleepingMonitorTableSession => {
        const session: SleepingMonitorTableSession = {
            col_order: defaultSleepingMonitorColOrder,
            columns: defaultSleepingMonitorColumns,
        };
        return session;
    };

const defaultConsumersColOrder = [
    'email',
    'name',
    'address',
    'phone',
    'uid',
    'lastLoginApp',
    'deviceCount',
    'readonly',
];
const defaultConsumersColumns = {
    email: true,
    name: true,
    address: true,
    phone: false,
    uid: false,
    lastLoginApp: true,
    deviceCount: true,
    readonly: true,
};

export const createDefaultConsumersUserSession = (): ConsumersUserSession => {
    const session: ConsumersUserSession = {
        col_order: defaultConsumersColOrder,
        columns: defaultConsumersColumns,
    };
    return session;
};

const defaultConsumerDevicesColOrder = [''];
const defaultConsumerDevicesColumns = {};

export const createDefaultConsumerDevicesSession =
    (): ConsumersDevicesSession => {
        const session: ConsumersDevicesSession = {
            col_order: defaultConsumerDevicesColOrder,
            columns: defaultConsumerDevicesColumns,
        };
        return session;
    };
